import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  AddLineItemInput,
  AdminInvoiceDetail,
  AdminInvoiceListItem,
  BillableJob,
  ClientInvoiceDetail,
  ClientInvoiceListItem,
  CreateInvoiceInput,
  InvoiceStatusValue,
  InvoiceTotals,
  RecordPaymentInput,
  UpdateInvoiceInput,
  UpdateLineItemInput,
} from '@shared/schemas/invoice';
import type { ListResponse, RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/invoices and the customer's own list. ROADMAP.md §12. */

const ADMIN_ROOT = ['admin', 'invoices'] as const;
const CLIENT_ROOT = ['client', 'invoices'] as const;

/**
 * The badge tone per state — one mapping, so no screen invents its own.
 *
 * `void` is `cancelled`-toned: a void invoice is a normal, recorded answer.
 * Overdue is **not** a tone here because it is not a status — screens show it
 * as its own marker beside the real one.
 */
export const INVOICE_TONES: Record<InvoiceStatusValue, StatusTone> = {
  draft: 'pending',
  sent: 'scheduled',
  paid: 'completed',
  void: 'cancelled',
};

export interface AdminInvoiceListParams {
  sort: RecordSort;
  page: number;
  status: InvoiceStatusValue | 'overdue' | 'all';
  search?: string;
  clientId?: string;
}

export interface AdminInvoiceList extends ListResponse<AdminInvoiceListItem> {
  totals: InvoiceTotals;
}

/** What POST /:id/send answers: the outcome in words, beside the fresh view. */
export interface SendInvoiceResult {
  delivery: { status: 'sent' | 'no_email' | 'failed' | 'suppressed'; message: string };
  invoice: AdminInvoiceDetail;
}

function adminInvalidate(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ADMIN_ROOT }).then(() => undefined);
}

export function useAdminInvoices(params: AdminInvoiceListParams) {
  return useQuery({
    queryKey: [...ADMIN_ROOT, 'list', params],
    queryFn: () => api.get<AdminInvoiceList>(`/api/admin/invoices${queryString({ ...params })}`),
    placeholderData: (previous) => previous,
  });
}

export function useAdminInvoice(id: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_ROOT, 'detail', id],
    queryFn: () => api.get<AdminInvoiceDetail>(`/api/admin/invoices/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

/** The builder's picker: completed jobs of one client not yet on a live invoice. */
export function useBillableJobs(clientId: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_ROOT, 'billable-jobs', clientId],
    queryFn: () =>
      api.get<{ data: BillableJob[] }>(
        `/api/admin/invoices/billable-jobs${queryString({ clientId })}`,
      ),
    enabled: clientId !== undefined && clientId.length > 0,
  });
}

/** Refresh the one detail a mutation answered with, plus every list. */
function settle(queryClient: QueryClient, detail: AdminInvoiceDetail): Promise<AdminInvoiceDetail> {
  queryClient.setQueryData([...ADMIN_ROOT, 'detail', detail.id], detail);
  return adminInvalidate(queryClient).then(() => detail);
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) =>
      api.post<AdminInvoiceDetail>('/api/admin/invoices', input),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useUpdateInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInvoiceInput) =>
      api.patch<AdminInvoiceDetail>(`/api/admin/invoices/${id}`, input),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useAddJobsToInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobIds: string[]) =>
      api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/jobs`, { jobIds }),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useAddLineItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddLineItemInput) =>
      api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/lines`, input),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useUpdateLineItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { lineId: string } & UpdateLineItemInput) => {
      const { lineId, ...body } = input;
      return api.patch<AdminInvoiceDetail>(`/api/admin/invoices/${id}/lines/${lineId}`, body);
    },
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useRemoveLineItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      api.delete<AdminInvoiceDetail>(`/api/admin/invoices/${id}/lines/${lineId}`),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useSendInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SendInvoiceResult>(`/api/admin/invoices/${id}/send`, {}),
    onSuccess: (result) => settle(queryClient, result.invoice).then(() => result),
  });
}

export function useVoidInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/void`, { reason }),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

/** Answers with the NEW draft — the caller navigates to it. */
export function useReissueInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/reissue`, {}),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useRecordPayment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordPaymentInput) =>
      api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/payments`, input),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

export function useRefundPayment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      api.post<AdminInvoiceDetail>(`/api/admin/invoices/${id}/payments/${paymentId}/refund`, {}),
    onSuccess: (detail) => settle(queryClient, detail),
  });
}

// ---------------------------------------------------------------------------
// The customer's own
// ---------------------------------------------------------------------------

export function useClientInvoices(page: number) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'list', page],
    queryFn: () =>
      api.get<ListResponse<ClientInvoiceListItem>>(`/api/client/invoices${queryString({ page })}`),
    placeholderData: (previous) => previous,
  });
}

export function useClientInvoice(id: string | undefined) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'detail', id],
    queryFn: () => api.get<ClientInvoiceDetail>(`/api/client/invoices/${id ?? ''}`),
    enabled: id !== undefined,
  });
}
